import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, CheckCircle2, Info, Loader2, MessageCircle, Send } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { APPOINTMENT_REASONS } from "@/data/services";
import { SITE, TIME_SLOTS } from "@/data/site";
import { buildAppointmentWhatsAppMessage, buildWhatsAppUrl, formatPhoneBR } from "@/lib/whatsapp";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const schema = z.object({
  name: z.string().trim().min(2, "Informe seu nome completo.").max(120, "Nome muito longo."),
  phone: z
    .string()
    .trim()
    .refine((v) => v.replace(/\D/g, "").length >= 10, "Informe um telefone válido com DDD."),
  email: z.string().trim().email("Informe um e-mail válido."),
  date: z.date({ required_error: "Escolha a data desejada.", invalid_type_error: "Escolha a data desejada." }),
  time: z.string().min(1, "Escolha um horário."),
  reason: z.string().min(1, "Selecione o motivo da consulta."),
  notes: z.string().trim().max(1000, "Observações muito longas.").optional().or(z.literal("")),
});

const today = new Date();
today.setHours(0, 0, 0, 0);

export const AppointmentForm = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { name: "", phone: "", email: "", date: undefined, time: "", reason: "", notes: "" },
    mode: "onBlur",
  });

  const onSubmit = async (values) => {
    setSubmitting(true);
    const payload = {
      name: values.name,
      phone: values.phone,
      email: values.email,
      date: format(values.date, "yyyy-MM-dd"),
      time: values.time,
      reason: values.reason,
      notes: values.notes || "",
    };
    try {
      const { data } = await axios.post(`${API}/appointments`, payload);
      const dateLabel = format(values.date, "dd/MM/yyyy", { locale: ptBR });
      const whatsappUrl = buildWhatsAppUrl(
        buildAppointmentWhatsAppMessage({ ...payload, dateLabel })
      );
      setSuccess({ id: data.id, name: values.name, dateLabel, time: values.time, whatsappUrl });
      toast.success("Solicitação recebida", {
        description: "Entraremos em contato para confirmar o horário.",
      });
      form.reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Não foi possível enviar sua solicitação. Tente novamente ou fale conosco pelo WhatsApp.";
      toast.error("Ops, algo deu errado", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="agendamento"
      data-testid="appointment-section"
      className="section-padding relative scroll-mt-24 bg-[hsl(var(--brand-off-white))]"
    >
      <div className="container-site grid gap-14 lg:grid-cols-12 lg:gap-16">
        <Reveal className="flex flex-col gap-8 lg:col-span-5">
          <SectionHeading
            id="appointment"
            eyebrow="Agendamento"
            title="Solicite seu horário com tranquilidade."
            description="Preencha o formulário com a data e o horário de sua preferência. Nossa equipe entrará em contato para confirmar a disponibilidade."
          />

          <div
            className="flex gap-4 rounded-[var(--radius)] border border-[hsl(var(--brand-champagne-gold))]/50 bg-card p-5"
            data-testid="appointment-disclaimer"
          >
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(var(--brand-champagne-gold))]" />
            <p className="text-sm leading-relaxed text-foreground">
              <strong className="font-semibold">Importante:</strong> esta é uma solicitação. O horário não é
              confirmado automaticamente — nossa equipe entrará em contato para confirmar.
            </p>
          </div>

          <div className="flex flex-col gap-3 text-sm text-muted-foreground">
            <p>Prefere falar diretamente com a equipe?</p>
            <Button
              asChild
              variant="secondary"
              className="h-12 w-fit rounded-xl border border-border px-6 active:scale-[0.98] transition-[background-color,transform] duration-200"
            >
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="appointment-direct-whatsapp-button"
              >
                <MessageCircle className="h-4 w-4" />
                {SITE.whatsappDisplay}
              </a>
            </Button>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <div className="card-premium p-6 sm:p-8">
            {success ? (
              <div
                className="flex flex-col items-start gap-6"
                data-testid="appointment-success-message"
                role="status"
                aria-live="polite"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(var(--brand-champagne-gold))]/20 text-[hsl(var(--brand-soft-brown))]">
                  <CheckCircle2 className="h-7 w-7" />
                </div>
                <div className="flex flex-col gap-3">
                  <h3 className="font-heading text-2xl font-semibold text-foreground">
                    Solicitação recebida, {success.name.split(" ")[0]}!
                  </h3>
                  <p className="text-base leading-relaxed text-muted-foreground">
                    Registramos seu pedido para <strong className="text-foreground">{success.dateLabel}</strong> às{" "}
                    <strong className="text-foreground">{success.time}</strong>. Entraremos em contato para confirmar
                    o horário. Se preferir agilizar, envie sua solicitação também pelo WhatsApp.
                  </p>
                  <p className="text-xs text-muted-foreground">Código da solicitação: {success.id.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button
                    asChild
                    className="h-12 rounded-xl px-6 active:scale-[0.98] transition-[background-color,transform] duration-200"
                  >
                    <a
                      href={success.whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="appointment-whatsapp-button"
                    >
                      <MessageCircle className="h-4 w-4" />
                      Enviar pelo WhatsApp
                    </a>
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-12 rounded-xl border border-border px-6"
                    onClick={() => setSuccess(null)}
                    data-testid="appointment-new-request-button"
                  >
                    Nova solicitação
                  </Button>
                </div>
              </div>
            ) : (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="flex flex-col gap-6"
                  data-testid="appointment-request-form"
                  noValidate
                >
                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome completo</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Seu nome"
                              autoComplete="name"
                              className="h-12 rounded-xl bg-background"
                              data-testid="appointment-name-input"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage data-testid="appointment-name-error" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Telefone / WhatsApp</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="(33) 99999-9999"
                              inputMode="tel"
                              autoComplete="tel"
                              className="h-12 rounded-xl bg-background"
                              data-testid="appointment-phone-input"
                              {...field}
                              onChange={(e) => field.onChange(formatPhoneBR(e.target.value))}
                            />
                          </FormControl>
                          <FormMessage data-testid="appointment-phone-error" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>E-mail</FormLabel>
                          <FormControl>
                            <Input
                              type="email"
                              placeholder="voce@exemplo.com"
                              autoComplete="email"
                              className="h-12 rounded-xl bg-background"
                              data-testid="appointment-email-input"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage data-testid="appointment-email-error" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="reason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Motivo da consulta</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger
                                className="h-12 rounded-xl bg-background"
                                data-testid="appointment-reason-select"
                              >
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="z-[var(--z-dropdown)] rounded-xl">
                              {APPOINTMENT_REASONS.map((r) => (
                                <SelectItem key={r} value={r} data-testid={`appointment-reason-option-${r}`}>
                                  {r}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage data-testid="appointment-reason-error" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-5 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="date"
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormLabel>Data desejada</FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  type="button"
                                  variant="outline"
                                  data-testid="appointment-date-button"
                                  className={cn(
                                    "h-12 justify-start rounded-xl bg-background px-3 text-left font-normal shadow-none hover:bg-accent",
                                    !field.value && "text-muted-foreground"
                                  )}
                                >
                                  <CalendarDays className="mr-2 h-4 w-4" />
                                  {field.value
                                    ? format(field.value, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                                    : "Escolha uma data"}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="z-[var(--z-dropdown)] w-auto rounded-xl p-0" align="start">
                              <Calendar
                                mode="single"
                                locale={ptBR}
                                selected={field.value}
                                onSelect={field.onChange}
                                disabled={(d) => d < today || d.getDay() === 0}
                                initialFocus
                                data-testid="appointment-date-calendar"
                              />
                            </PopoverContent>
                          </Popover>
                          <FormMessage data-testid="appointment-date-error" />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="time"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Horário desejado</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-12 rounded-xl bg-background" data-testid="appointment-time-select">
                                <SelectValue placeholder="Selecione" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="z-[var(--z-dropdown)] max-h-64 rounded-xl">
                              {TIME_SLOTS.map((t) => (
                                <SelectItem key={t} value={t} data-testid={`appointment-time-option-${t}`}>
                                  {t}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage data-testid="appointment-time-error" />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="notes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          Observações <span className="font-normal text-muted-foreground">(opcional)</span>
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Conte um pouco sobre o que você precisa ou alguma preferência de atendimento."
                            rows={4}
                            className="min-h-[112px] rounded-xl bg-background"
                            data-testid="appointment-notes-textarea"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage data-testid="appointment-notes-error" />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-relaxed text-muted-foreground sm:max-w-xs">
                      Ao enviar, você concorda em ser contatado pela nossa equipe para confirmar o horário.
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="appointment-submit-button"
                      className="h-12 rounded-xl px-7 text-base active:scale-[0.98] transition-[background-color,transform] duration-200"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {submitting ? "Enviando..." : "Enviar solicitação"}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
